import React, { useEffect, useState, useRef } from 'react';
import { Calendar, ChevronLeft, ChevronRight, X } from 'lucide-react';
interface DatePickerProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  maxDate?: string; // YYYY-MM-DD format, defaults to today
  disableFuture?: boolean;
}
const MONTHS = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
const DAYS = ['Lu', 'Ma', 'Mi', 'Ju', 'Vi', 'Sa', 'Do'];
function getDaysInMonth(year: number, month: number) {
  return new Date(year, month + 1, 0).getDate();
}
function getFirstDayOfMonth(year: number, month: number) {
  const day = new Date(year, month, 1).getDay();
  return day === 0 ? 6 : day - 1;
}
export function DatePicker({
  value,
  onChange,
  placeholder = 'Seleccionar fecha',
  className = '',
  disableFuture = true
}: DatePickerProps) {
  const [isOpen, setIsOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const today = new Date();
  const todayYear = today.getFullYear();
  const todayMonth = today.getMonth();
  const todayDay = today.getDate();
  const parsedValue = value ? new Date(value + 'T00:00:00') : null;
  const [viewYear, setViewYear] = useState(parsedValue?.getFullYear() || todayYear);
  const [viewMonth, setViewMonth] = useState(parsedValue?.getMonth() || todayMonth);
  useEffect(() => {
    if (value) {
      const d = new Date(value + 'T00:00:00');
      setViewYear(d.getFullYear());
      setViewMonth(d.getMonth());
    }
  }, [value]);
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  const daysInMonth = getDaysInMonth(viewYear, viewMonth);
  const firstDay = getFirstDayOfMonth(viewYear, viewMonth);
  // Check if a day is in the future
  const isFutureDay = (day: number) => {
    if (!disableFuture) return false;
    if (viewYear > todayYear) return true;
    if (viewYear === todayYear && viewMonth > todayMonth) return true;
    if (viewYear === todayYear && viewMonth === todayMonth && day > todayDay) return true;
    return false;
  };
  // Check if next month button should be disabled
  const isNextMonthDisabled = () => {
    if (!disableFuture) return false;
    if (viewYear > todayYear) return true;
    if (viewYear === todayYear && viewMonth >= todayMonth) return true;
    return false;
  };
  const handlePrevMonth = () => {
    if (viewMonth === 0) {
      setViewMonth(11);
      setViewYear(viewYear - 1);
    } else {
      setViewMonth(viewMonth - 1);
    }
  };
  const handleNextMonth = () => {
    if (isNextMonthDisabled()) return;
    if (viewMonth === 11) {
      setViewMonth(0);
      setViewYear(viewYear + 1);
    } else {
      setViewMonth(viewMonth + 1);
    }
  };
  const handleSelectDay = (day: number) => {
    if (isFutureDay(day)) return;
    const dateStr = `${viewYear}-${String(viewMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    onChange(dateStr);
    setIsOpen(false);
  };
  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange('');
    setIsOpen(false);
  };
  const handleToday = () => {
    const t = new Date();
    const dateStr = `${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, '0')}-${String(t.getDate()).padStart(2, '0')}`;
    onChange(dateStr);
    setViewYear(t.getFullYear());
    setViewMonth(t.getMonth());
    setIsOpen(false);
  };
  const formatDisplay = (val: string) => {
    if (!val) return '';
    const parts = val.split('-');
    return `${parts[2]}/${parts[1]}/${parts[0]}`;
  };
  const isToday = (day: number) => {
    return day === todayDay && viewMonth === todayMonth && viewYear === todayYear;
  };
  const isSelected = (day: number) => {
    if (!parsedValue) return false;
    return day === parsedValue.getDate() && viewMonth === parsedValue.getMonth() && viewYear === parsedValue.getFullYear();
  };
  return <div ref={ref} className={`relative ${className}`} data-id="element-1125">
      <button type="button" onClick={() => setIsOpen(!isOpen)} className="w-full h-10 px-3 flex items-center gap-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-sm text-left hover:border-gray-300 dark:hover:border-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-900 dark:focus:ring-white focus:border-transparent transition-all cursor-pointer" data-id="element-1126">
        <Calendar size={15} className="text-gray-400 shrink-0" data-id="element-1127" />
        <span className={`flex-1 truncate ${value ? 'text-gray-900 dark:text-white' : 'text-gray-400 dark:text-gray-500'}`} data-id="element-1128">
          {value ? formatDisplay(value) : placeholder}
        </span>
        {value && <span onClick={handleClear} className="p-0.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors" data-id="element-1129">
            <X size={14} className="text-gray-400" data-id="element-1130" />
          </span>}
      </button>

      {isOpen && <div className="absolute top-full left-0 mt-1.5 z-50 w-72 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl shadow-xl shadow-gray-200/50 dark:shadow-black/30 overflow-hidden" data-id="element-1131">
          {/* Month/Year header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 dark:border-gray-800" data-id="element-1132">
            <button type="button" onClick={handlePrevMonth} className="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors text-gray-600 dark:text-gray-300" data-id="element-1133">
              <ChevronLeft size={16} data-id="element-1134" />
            </button>
            <span className="text-sm font-bold text-gray-900 dark:text-white" data-id="element-1135">
              {MONTHS[viewMonth]} {viewYear}
            </span>
            <button type="button" onClick={handleNextMonth} disabled={isNextMonthDisabled()} className={`p-1.5 rounded-lg transition-colors ${isNextMonthDisabled() ? 'text-gray-200 dark:text-gray-700 cursor-not-allowed' : 'hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-600 dark:text-gray-300'}`} data-id="element-1136">
              <ChevronRight size={16} data-id="element-1137" />
            </button>
          </div>

          {/* Day headers */}
          <div className="grid grid-cols-7 px-3 pt-3 pb-1" data-id="element-1138">
            {DAYS.map(day => <div key={day} className="text-center text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-wider py-1" data-id="element-1139">
                {day}
              </div>)}
          </div>

          {/* Day grid */}
          <div className="grid grid-cols-7 px-3 pb-3 gap-0.5" data-id="element-1140">
            {Array.from({
          length: firstDay
        }).map((_, i) => <div key={`empty-${i}`} className="h-8" data-id="element-1141" />)}
            {Array.from({
          length: daysInMonth
        }).map((_, i) => {
          const day = i + 1;
          const selected = isSelected(day);
          const todayMark = isToday(day);
          const future = isFutureDay(day);
          return <button key={day} type="button" onClick={() => handleSelectDay(day)} disabled={future} className={`h-8 w-full rounded-lg text-xs font-medium transition-all ${future ? 'text-gray-200 dark:text-gray-700 cursor-not-allowed' : selected ? 'bg-gray-900 dark:bg-white text-white dark:text-gray-900 font-bold shadow-sm' : todayMark ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 font-bold ring-1 ring-blue-200 dark:ring-blue-800' : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'}`} data-id="element-1142">
                  {day}
                </button>;
        })}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between px-4 py-2.5 border-t border-gray-100 dark:border-gray-800 bg-gray-50/50 dark:bg-gray-800/30" data-id="element-1143">
            <button type="button" onClick={() => {
          onChange('');
          setIsOpen(false);
        }} className="text-xs font-medium text-gray-500 dark:text-gray-400 hover:text-red-600 dark:hover:text-red-400 transition-colors" data-id="element-1144">
              Limpiar
            </button>
            <button type="button" onClick={handleToday} className="text-xs font-bold text-gray-900 dark:text-white hover:text-blue-600 dark:hover:text-blue-400 transition-colors" data-id="element-1145">
              Hoy
            </button>
          </div>
        </div>}
    </div>;
}